import cx from 'classnames';
import {Form, Formik, FormikProps} from 'formik';
import {keys, values, mapValues, keyBy} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {accountOAuthApi} from 'api';
import {
    ConfirmModal,
    PredefinedNotifierType,
    InputFormik,
    ToggleFormik,
    FormControls,
    RequiredMark
} from 'components';
import {useApiData, usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {OAuthClient, OAuthScope} from 'types';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS.OAUTH;

type Props = {
    isOpen: boolean;
    data?: OAuthClient;
    availableScopes: OAuthScope[];
    onClose: (confirm: boolean, data?: CreateClientFormFields) => void;
    onSuccess: (data: any) => void;
}

type CreateClientFormFields = {
    name: string;
    scopes: {[key in OAuthScope]: boolean};
}

const SCOPE_DESCRIPTION_MAP = {
    [OAuthScope.AppointmentsRead]: T_KEYS.SCOPES.APPOINTMENTS_READ_DESC,
    [OAuthScope.AppointmentsCreate]: T_KEYS.SCOPES.APPOINTMENTS_CREATE_DESC,
    [OAuthScope.AppointmentsUpdate]: T_KEYS.SCOPES.APPOINTMENTS_UPDATE_DESC,
    [OAuthScope.AppointmentsDelete]: T_KEYS.SCOPES.APPOINTMENTS_DELETE_DESC,
    [OAuthScope.ClientConnectionsRead]: T_KEYS.SCOPES.CLIENT_CONNECTIONS_READ_DESC,
    [OAuthScope.ClientConnectionsCreate]: T_KEYS.SCOPES.CLIENT_CONNECTIONS_CREATE_DESC,
    [OAuthScope.ClientConnectionsUpdate]: T_KEYS.SCOPES.CLIENT_CONNECTIONS_UPDATE_DESC,
    [OAuthScope.ClientConnectionsDelete]: T_KEYS.SCOPES.CLIENT_CONNECTIONS_DELETE_DESC,
    [OAuthScope.ClientConnectionsNotify]: T_KEYS.SCOPES.CLIENT_CONNECTIONS_NOTIFY_DESC,
    [OAuthScope.SessionsOnlineSummary]: T_KEYS.SCOPES.SESSIONS_ONLINE_SUMMARY_DESC,
    [OAuthScope.SessionsParticipantUpdate]: T_KEYS.SCOPES.SESSIONS_PARTICIPANT_UPDATE_DESC,
    [OAuthScope.UsersAvailability]: T_KEYS.SCOPES.USERS_AVAILABILITY_DESC,
    [OAuthScope.SettingsUserInfoSchemaRead]: T_KEYS.SCOPES.SETTINGS_USER_INFO_SCHEMA_READ_DESC,
};

type FormTemplateProps = {
    open: boolean;
    title: string;
    okDisabled: boolean;
    availableScopes: OAuthScope[];
    onClose: (confirm: boolean, data?: CreateClientFormFields) => void;
} & FormikProps<CreateClientFormFields>

const FormTemplate: React.FC<FormTemplateProps> = ({open, title, okDisabled, availableScopes, onClose, values: formValues, resetForm}) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);

    const scopeOptions = useMemo(() => values(OAuthScope)
        .filter((scope) => availableScopes.includes(scope))
        .map((scope) => ({
            scope,
            description: t(SCOPE_DESCRIPTION_MAP[scope])
        })), [t, availableScopes]);

    useEffect(() => {
        if (!open && prevOpen) {
            resetForm();
        }
    }, [open, prevOpen, resetForm]);

    return (
        <ConfirmModal
            open={open}
            title={title}
            maxWidth='sm'
            fullWidth
            okDisabled={okDisabled}
            onClose={(confirm: boolean) => onClose(confirm, formValues)}
        >
            <Form>
                <FormControls>
                    <div className='tw-ts-base tw-text-default-dimmed'>{t(T_KEYS.CREATE_MODAL.DESCRIPTION)}</div>

                    <InputFormik
                        name='name'
                        label={t(T_KEYS.CREATE_MODAL.NAME_LABEL)}
                        required
                    />

                    <div className='tw-mt-4'>
                        <div className='tw-ts-base-bold tw-text-dark tw-mb-3'>
                            {t(T_KEYS.CREATE_MODAL.SELECT_SCOPES)}
                            <RequiredMark className='tw-ml-1' />
                        </div>

                        <FormControls>
                            {scopeOptions.map(({scope, description}) => {
                                const isSelected = formValues.scopes[scope];

                                return (
                                    <div
                                        key={scope}
                                        className={cx(
                                            'tw-border tw-rounded-lg tw-p-3 tw-transition-colors',
                                            {
                                                'tw-border-primary': isSelected,
                                                'tw-border-default': !isSelected
                                            }
                                        )}
                                    >
                                        <div className='tw-flex tw-items-center tw-justify-between'>
                                            <div>
                                                <div className='tw-ts-base-bold tw-text-dark'>{scope}</div>
                                                <div className='tw-text-sm tw-text-default-dimmed tw-mt-1'>
                                                    {description}
                                                </div>
                                            </div>
                                            <ToggleFormik
                                                name={`scopes.${scope}`}
                                                size='small'
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </FormControls>
                    </div>
                </FormControls>
            </Form>
        </ConfirmModal>
    );
};

export const OAuthDetailsModal: React.FC<Props> = ({
    isOpen,
    data,
    availableScopes,
    onClose,
    onSuccess,
}) => {
    const {t} = useTranslation();

    const editMode = useMemo(() => !!data, [data]);

    const initialValues = useMemo(() => ({
        name: data?.name || '',
        scopes: mapValues(
            keyBy(availableScopes),
            (scope) => data?.scopes?.includes(scope as OAuthScope) || false
        ) as CreateClientFormFields['scopes']
    }), [data, availableScopes]);

    const {send: createClient, loading: createLoading} = useApiData(
        accountOAuthApi.create,
        {
            onSuccess,
            notificationType: PredefinedNotifierType.Save
        }
    );

    const {send: updateClient, loading: updateLoading} = useApiData(
        accountOAuthApi.update,
        {
            onSuccess,
            notificationType: PredefinedNotifierType.Save
        }
    );

    const loading = useMemo(() => createLoading || updateLoading, [createLoading, updateLoading]);

    const modalTitle = useMemo(() => editMode ? t(T_KEYS.CREATE_MODAL.EDIT_TITLE) : t(T_KEYS.CREATE_MODAL.TITLE), [editMode, t]);

    const isFormValid = useCallback((formValues: CreateClientFormFields) => {
        const hasSelectedScopes = values(formValues.scopes).some(Boolean);
        const hasValidName = formValues.name.trim().length > 0;
        return hasSelectedScopes && hasValidName;
    }, []);

    const handleSubmit = useCallback((formValues: CreateClientFormFields) => {
        if (!isFormValid(formValues)) {
            return;
        }
        const selectedScopes = keys(formValues.scopes).filter((scope) => formValues.scopes[scope as OAuthScope]) as OAuthScope[];

        const payload = {...formValues, scopes: selectedScopes};
        if (data) {
            updateClient(data.id, payload);
        } else {
            createClient(payload);
        }
    }, [isFormValid, data, updateClient, createClient]);

    const handleClose = useCallback((confirm: boolean, formValues?: CreateClientFormFields) => {
        if (confirm && formValues && isFormValid(formValues)) {
            handleSubmit(formValues);
            return;
        }
        onClose(confirm, formValues);
    }, [handleSubmit, onClose, isFormValid]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    open={isOpen}
                    title={modalTitle}
                    availableScopes={availableScopes}
                    okDisabled={!isFormValid(props.values) || loading}
                    onClose={handleClose}
                />
            )}
        </Formik>
    );
};